import { useEffect, useState, type ReactNode } from 'react';
import {
  Size, TITLE_BAR_COLLAPSED_KEY, assertBannerShape, footerVisible, t, titleBarCollapsed, titleBarPreferenceOf,
  type BannerRight, type IconMeaning, type TitleBarPreference,
} from '@bms2/shared';
import { Icon } from '../components/Icon';
import { IconButton } from '../components/controls';
import styles from './shell.module.css';
import { readPreference, writePreference } from './devicePreference';
import { usePhoneWidth } from '../components/usePhoneWidth';

/**
 * The Page Module shell (piece 12): a Title Banner, a Content region and a
 * conditional Footer. 🔨 Right-aligned the banner carries either the status Pill
 * or a list page's primary New button, never both — the shape check refuses the
 * pair rather than rendering it. Content always renders, in a per-screen shape.
 *
 * 🔒 **The title bar standard** (T170, on the product owner's words of
 * 2026-09-22): the picture slot at the left, the name, one kind line beneath it,
 * the status pills and the Edit control as an icon at the far right. Nothing
 * else but the one summary column a record type's registry entry names (decision
 * 0665). There is no facts row and no contact column, and a document's own
 * details read in its Details view and on its sheet. 🔨 A page needing a fact in
 * the banner has the fact in the wrong place, not the banner in the wrong shape.
 */
export interface PageModuleProps {
  title: string;
  /**
   * 🔒 The one kind line under the name, which is what `pageKindLine` answers
   * (T176). It takes either a line of text or the classification pills that stand
   * in its place on a Company and a Person (T175). 🔨 It is not a second title
   * and never a fact about the record.
   */
  subtitle?: ReactNode;
  /** The record type's glyph left of the title; it is dropped under the md breakpoint where the banner has no room to spare (product owner, 2026-09-10). */
  icon?: IconMeaning | null;
  /**
   * The picture slot at the banner's left (decision 0616; T170): the record's own
   * picture where it has one, the default avatar where it has none. It stands
   * where the record type's glyph stands and replaces it. A list page and a
   * register fill it with nothing and keep the glyph.
   */
  avatar?: ReactNode;
  /** Small pills under the title: a Company's classifications, a Person's employment badge (decision 0335). */
  kinds?: ReactNode;
  recordNumber?: string | null;
  onEdit?: () => void;
  /** The status pill, for a record that has one. */
  status?: ReactNode;
  /**
   * 🔒 The summary column between the name and the status pill (decision 0665,
   * amending 0621). 🔨 A record page passes it only where its type's registry
   * entry names one, and the Deal is the one that does: its value and its Next
   * Action. Under the md breakpoint it takes a line of its own beneath the name.
   */
  column?: ReactNode;
  /** A list page's primary New button. */
  newAction?: ReactNode;
  /**
   * A page's own view controls, drawn inside the banner between the title and
   * the right-hand slot and spread across it (decision 0329). Under the md
   * breakpoint they wrap onto their own line beneath the title.
   */
  controls?: ReactNode;
  /**
   * 🔒 The controls are a help ring and no view control, so on a phone they stand
   * on the title's own row, beside the status pill, and never take a row of their
   * own (decision 3300; T2272). Above md nothing changes.
   */
  controlsInline?: boolean;
  /**
   * 🔒 The toolbar row directly beneath the banner (decision 1440; T685). The
   * banner holds at most one action at its right, the page's + New or the
   * record's Edit, and every other page-wide action stands here instead, where
   * it reads as a control rather than fighting the navy.
   */
  toolbar?: ReactNode;
  /**
   * 🔒 The gear to the settings that govern the page, at the right end of the title
   * bar after the banner's one action (decision 2815; T2056, amending 2805). It is
   * not that action: it opens a menu of links and edits nothing. A page hands it
   * here and never to `toolbar`, so the toolbar row is drawn only for what else it
   * carries and no band stands with the gear alone in it.
   */
  gear?: ReactNode;
  footer?: ReactNode;
  /**
   * 🔒 A record page's title bar folds on a phone whenever it stacks (piece 13;
   * decision 1472, amending T341): its first line, the picture, the name and the
   * status pill, and a chevron that opens it. Folded is the default there, and
   * the viewer's choice is remembered in this browser. A bar with nothing beneath
   * its first line draws whole with no chevron.
   */
  collapsible?: boolean;
  children: ReactNode;
}

function readTitleBarPreference(): TitleBarPreference | null {
  return readPreference<TitleBarPreference | null>(TITLE_BAR_COLLAPSED_KEY, (raw) => titleBarPreferenceOf(raw), null);
}

function rememberTitleBarPreference(preference: TitleBarPreference): void {
  writePreference(TITLE_BAR_COLLAPSED_KEY, preference);
}

/**
 * 🔒 Decision 2152: a name that would wrap draws one size step smaller; one that still
 * does not fit on two lines is cut by the CSS clamp. Measured, never keyed on length,
 * because the room beside the pills decides at the moment of the measure: the step resets
 * whenever the name or the bar's width changes and is read again at the larger size first.
 * 🔨 A change only to the room beside the name (the phone bar collapsing or expanding, a
 * status or classification pill) does not reset it, so the name can stay one step smaller
 * than needed until the name or the bar's width next changes (T1822); resetting on that
 * change would bring back the flicker T1877 ended.
 *
 * 🔨 The width is the bar's own, never the heading's container (T1877). The name's
 * column is as wide as its text up to the room it has, so the smaller size narrowed
 * it, the narrower width reset the step, the larger size widened it again and the
 * header flipped between the two sizes several times a second. The bar's width does
 * not move with the name's size, and for one name at one width the step is only ever
 * taken, never undone, so the measure settles on its second read.
 */
function useTitleStep(title: string) {
  const [node, setNode] = useState<HTMLHeadingElement | null>(null);
  const [width, setWidth] = useState(0);
  const [stepped, setStepped] = useState<string | null>(null);
  const key = `${title}|${width}`;
  useEffect(() => {
    const parent = node?.parentElement;
    if (!node || !parent) return undefined;
    const bar = node.closest('header') ?? parent;
    /* The larger size is drawn first for every new name or width; a step is made only from a measure taken at that key. */
    const measure = () => {
      const line = parseFloat(getComputedStyle(node).lineHeight);
      const now = Math.round(bar.getBoundingClientRect().width);
      setWidth(now);
      if (Number.isFinite(line) && line > 0 && node.scrollHeight > line * 1.5) setStepped(`${title}|${now}`);
    };
    measure();
    if (typeof ResizeObserver === 'undefined') return undefined;
    const watch = new ResizeObserver(measure);
    watch.observe(bar);
    watch.observe(node);
    return () => watch.disconnect();
  }, [node, title]);
  return { setNode, smaller: stepped === key };
}

export function PageModule({ title, subtitle, icon, avatar, recordNumber, onEdit, status, column, newAction, controls, controlsInline = false, toolbar, gear, footer, children, kinds, collapsible = false }: PageModuleProps) {
  const { setNode: titleRef, smaller: titleSmaller } = useTitleStep(title);
  /* 🔒 Decision 2152: the whole name stands in the tab title wherever the bar cuts it. */
  useEffect(() => {
    const before = document.title;
    document.title = `${title} · ${t('app.name')}`;
    return () => { document.title = before; };
  }, [title]);
  const right: BannerRight = assertBannerShape({ hasStatus: status != null, hasNewAction: newAction != null });
  /* 🔒 The footer is drawn when the page passed something to draw in it (T113); a status pill decides nothing here. */
  const showsFooter = footerVisible({ hasFooterContent: footer != null });
  const phone = usePhoneWidth() && collapsible;
  const [preference, setPreference] = useState<TitleBarPreference | null>(() => (collapsible ? readTitleBarPreference() : null));
  /* 🔒 Something beneath the first line to fold: under the md breakpoint each of these drops under the name, so the bar stacks (decision 1472). */
  const foldable = Boolean(subtitle) || Boolean(kinds) || Boolean(recordNumber) || controls != null || column != null;
  const collapsed = titleBarCollapsed({ phone, foldable, preference });
  const toggle = () => {
    const next: TitleBarPreference = collapsed ? 'expanded' : 'collapsed';
    setPreference(next);
    rememberTitleBarPreference(next);
  };
  const offersToggle = phone && foldable;

  return (
    <main className={styles.page} data-shell="page">
      <header className={styles.banner} data-banner-right={right} data-banner-controls={controls != null && !collapsed ? 'true' : 'false'} {...(controls != null && !collapsed && controlsInline ? { 'data-banner-controls-place': 'inline' } : {})} data-banner-column={column != null && !collapsed ? 'true' : 'false'} data-banner-collapsed={offersToggle ? String(collapsed) : undefined}>
        {/* 🔨 The avatar replaces the glyph rather than standing beside it (decision 0616): two pictures of the same record in the same slot say nothing twice. */}
        {avatar != null ? (
          <span className={styles.bannerAvatar} data-shell="banner-avatar">{avatar}</span>
        ) : icon ? (
          // 🔨 A fixed size, three quarters of the banner at its ordinary height; it does not grow when the pills stack (decision 0335).
          <span className={styles.bannerIcon} data-shell="banner-icon" aria-hidden="true">
            <Icon meaning={icon} size="banner" />
          </span>
        ) : null}
        <div className={styles.bannerText}>
          {/* 🔨 The name and the record's own pills are one row above md, so the banner
              is two rows of text and no more (product owner, 2026-09-13). */}
          <div className={styles.bannerHead} data-shell="banner-head">
            <h1 ref={titleRef} className={styles.bannerTitle} title={title} {...(titleSmaller ? { 'data-title-step': 'smaller' } : {})}>{title}</h1>
            {kinds && !collapsed ? <div className={styles.bannerKinds} data-shell="banner-kinds" aria-label={t('shell.bannerKinds')}>{kinds}</div> : null}
          </div>
          {/* 🔒 One kind line under the name and nothing beneath it (T170; T176): the record type's own name, the classification pills that stand in its place, or the page's own registered line. */}
          {subtitle && !collapsed ? <p className={styles.bannerSubtitle} data-shell="banner-kind-line">{subtitle}</p> : null}
        </div>
        {recordNumber && !collapsed && <span data-shell="record-number">{recordNumber}</span>}
        {controls != null && !collapsed ? <div className={styles.bannerControls} data-shell="banner-controls">{controls}</div> : null}
        {column != null && !collapsed ? <div className={styles.bannerColumn} data-shell="banner-column">{column}</div> : null}
        {/* 🔒 The Edit control is an icon at the far right of the banner, past the status pill (T170, product owner 2026-09-22: "Place the edit icon in the title bar on far right"). */}
        <div className={styles.bannerRight}>
          {status ?? newAction}
          {/* 🔒 Through the one icon button control, not a button of this stylesheet's own (T178). */}
          {onEdit && <IconButton meaning="edit" label={t('shell.editRecord')} size={Size.Small} tone="onFill" name="banner-edit" onClick={onEdit} />}
          {/* 🔒 Decision 2815: the settings gear stands after the one action and is not it. */}
          {gear != null ? <span data-shell="banner-gear">{gear}</span> : null}
          {/* 🔒 Decision 1472: the chevron stands on a phone alone, wherever the bar has something beneath its first line to fold. */}
          {offersToggle ? <IconButton meaning={collapsed ? 'expand' : 'collapse'} label={t(collapsed ? 'shell.titleBarExpand' : 'shell.titleBarCollapse')} size={Size.Small} tone="onFill" name="banner-collapse" onClick={toggle} /> : null}
        </div>
      </header>
      {toolbar != null ? <div className={styles.pageToolbar} data-shell="page-toolbar">{toolbar}</div> : null}
      <div className={styles.content} data-shell="content">{children}</div>
      {showsFooter && <footer className={styles.footer} data-shell="page-footer">{footer}</footer>}
    </main>
  );
}
